import * as React from 'react';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import type { AccountPermissionTemplate } from '@acceligentllc/shared/enums/accountPermissionTemplate';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import { useToggle } from 'af-utils/react.util';

import WarningModal from 'af-components/ConfirmationModal';

import Actions from './Actions';
import ExcludeNotifyAll from './ExcludeNotifyAll';
import type FormModel from './formModel';
import Invitation from './Invitation';
import LMSActivation from './LMSActivation';
import MemberPermissions from './MemberPermissions';
import Section from './Section';
import UserGroup from './UserGroup';
import UserWaiverStatus from './UserWaiverStatus';

const SAVE_USER_WITH_WAIVER_HEADER = 'Confirm Waiver Status Update';

interface OwnProps {
	invalidEmail: boolean;
	invalidPhoneNumber: boolean;
	isCompanyAdmin: boolean;
	permissions: FormModel['permissions'];
	submitting: boolean;
	invalid: boolean;
	showActivateImmediately: boolean;
	assignableToWorkOrder: boolean;
	assignableAsSuperintendent: boolean;
	assignableAsAccounting: boolean;
	assignableAsManagement: boolean;
	assignableAsTechnician: boolean;
	accountTemplate: AccountPermissionTemplate;
	activeInLMS: boolean;
	excludeFromNotifyAll: boolean;
	/** YYYY-MM-DD */
	timeRoundingPolicyFrom: Nullable<string>;
	companyWaiverSetting: boolean;
	/** MM-DD-YYYY */
	waiverStatusValidFrom: Nullable<string>;
	/** MM-DD-YYYY */
	initialWaiverStatusValidFrom: Nullable<string>;
	onSubmit: () => void;
	onCancel: () => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	change: (filedName: string, value: any) => void;
}

type Props = React.PropsWithChildren<OwnProps>;

const MemberForm: React.FC<Props> = (props) => {
	const {
		children,
		invalidEmail,
		invalidPhoneNumber,
		isCompanyAdmin,
		permissions,
		submitting,
		invalid,
		showActivateImmediately,
		assignableToWorkOrder,
		assignableAsSuperintendent,
		assignableAsAccounting,
		assignableAsManagement,
		assignableAsTechnician,
		accountTemplate,
		activeInLMS,
		excludeFromNotifyAll,
		onCancel,
		onSubmit,
		change,
		waiverStatusValidFrom,
		initialWaiverStatusValidFrom,
		companyWaiverSetting,
		timeRoundingPolicyFrom,
	} = props;

	const { value: showWaiverWarningModal, toggle: toggleWaiverWarningModal } = useToggle(false);

	const SAVE_USER_WITH_WAIVER_BODY = React.useMemo(() => {
		let recalcDate = waiverStatusValidFrom;

		if (waiverStatusValidFrom && initialWaiverStatusValidFrom) {
			recalcDate = waiverStatusValidFrom < initialWaiverStatusValidFrom ? waiverStatusValidFrom : initialWaiverStatusValidFrom;
		} else if (!waiverStatusValidFrom && initialWaiverStatusValidFrom) {
			recalcDate = initialWaiverStatusValidFrom;
		}

		if (recalcDate) {
			return (
				<>
					<span>Once confirmed, missed meal break premium on the job payroll table </span>
					<span>for this user will be recalculated starting from </span>
					<span><b>{recalcDate}</b>.</span>
					<div>Are you sure you wish to continue? </div>
				</>);
		}
	}, [initialWaiverStatusValidFrom, waiverStatusValidFrom]);

	const showUserWaiverSetting = React.useMemo(() => {
		if (companyWaiverSetting && timeRoundingPolicyFrom) {
			return new Date() >= TimeUtils.parseDate(timeRoundingPolicyFrom, TimeFormat.DB_DATE_ONLY);
		}
		return false;
	}, [companyWaiverSetting, timeRoundingPolicyFrom]);

	const handleSubmit = React.useMemo(() => {
		if (waiverStatusValidFrom !== initialWaiverStatusValidFrom) {
			return toggleWaiverWarningModal;
		}
		return onSubmit;
	}, [initialWaiverStatusValidFrom, onSubmit, toggleWaiverWarningModal, waiverStatusValidFrom]);

	return (
		<div>
			<Section
				label="User Information"
			>
				{children}
			</Section>
			<Section
				label="Additional Options"
				labelTooltip={`
					Supplementary account settings controlling the user's LMS activity status and their inclusion in company-wide notification broadcasts.
				`}
			>
				{showActivateImmediately &&
					<Invitation
						invalidEmail={invalidEmail}
						invalidPhoneNumber={invalidPhoneNumber}
					/>
				}
				<LMSActivation isActivated={activeInLMS} />
				<ExcludeNotifyAll isExcluded={excludeFromNotifyAll} />
				{showUserWaiverSetting &&
					<UserWaiverStatus
						change={change}
						timeRoundingPolicyFrom={timeRoundingPolicyFrom!}
						waiverStatusValidFrom={waiverStatusValidFrom}
					/>
				}
			</Section>
			<Section
				label="User Groups"
				labelTooltip="Group assignments for this user, used to logically organize users and target them with group-level notifications. Assign groups by selecting the appropriate group tags."
			>
				<UserGroup />
			</Section>
			<Section
				label="Permissions"
				labelTooltip="Access control configuration for the user, defining their assignable roles and the specific system permissions granted across all functional areas of the application, including resources, equipment, jobs, and work orders."
			>
				<MemberPermissions
					accountTemplate={accountTemplate}
					assignableAsAccounting={assignableAsAccounting}
					assignableAsManagement={assignableAsManagement}
					assignableAsSuperintendent={assignableAsSuperintendent}
					assignableAsTechnician={assignableAsTechnician}
					assignableToWorkOrder={assignableToWorkOrder}
					change={change}
					isCompanyAdmin={isCompanyAdmin}
					permissions={permissions}
				/>
				<Actions
					invalid={invalid}
					onCancel={onCancel}
					onSubmit={handleSubmit}
					submitting={submitting}
				/>
			</Section>
			<WarningModal
				body={SAVE_USER_WITH_WAIVER_BODY}
				closeModal={toggleWaiverWarningModal}
				confirmAction={onSubmit}
				confirmText="Save"
				modalStyle="warning"
				showCancel={true}
				showModal={showWaiverWarningModal}
				size="md"
				title={SAVE_USER_WITH_WAIVER_HEADER}
			/>
		</div>
	);
};

export default MemberForm;
